/* global axios */
import ApiClient from './ApiClient';

function appendList(params, key, value) {
  if (!value) return;
  const list = Array.isArray(value) ? value : [value];
  list.filter(Boolean).forEach(item => params.append(`${key}[]`, item));
}

/**
 * Builds the shared filter query string. `get` and `export` both go through this, so a filter
 * can never apply on screen and be quietly dropped from the downloaded file.
 */
function buildParams({
  perPage,
  actionCategory,
  auditAction,
  actorId,
  actorType,
  source,
  resourceType,
  resourceId,
  parentType,
  parentId,
  humansOnly,
  direction,
  from,
  to,
} = {}) {
  const params = new URLSearchParams();

  // Repeatable filters arrive as arrays; Rails parses the [] suffix into an Array.
  // `audit_action`, not `action` — `action` is reserved by Rails routing (always resolves to
  // the controller action name) and can never carry a caller-supplied filter value.
  appendList(params, 'action_category', actionCategory);
  appendList(params, 'audit_action', auditAction);
  appendList(params, 'actor_type', actorType);
  appendList(params, 'source', source);

  if (perPage) params.append('per_page', perPage);
  if (actorId) params.append('actor_id', actorId);
  if (resourceType) params.append('resource_type', resourceType);
  if (resourceId) params.append('resource_id', resourceId);
  if (parentType) params.append('parent_type', parentType);
  if (parentId) params.append('parent_id', parentId);
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  if (direction) params.append('direction', direction);
  if (humansOnly !== undefined) params.append('humans_only', humansOnly);

  return params;
}

/**
 * Client for the audit_events endpoint, which supersedes audit_trail.
 *
 * Pagination is keyset, not page-number: the response returns `next_cursor` and you pass it
 * back as `cursor`. There is no total count — counting a partitioned audit table on every page
 * load is what made the previous endpoint slow.
 */
class AuditEvents extends ApiClient {
  constructor() {
    super('audit_events', { accountScoped: true });
  }

  get({ cursor, ...filters } = {}) {
    const params = buildParams(filters);

    if (cursor?.occurred_at) {
      params.append('cursor_occurred_at', cursor.occurred_at);
      params.append('cursor_id', cursor.id);
    }

    const query = params.toString();
    return axios.get(query ? `${this.url}?${query}` : this.url);
  }

  /**
   * Enqueues an export. Returns immediately — the file is built in a background job and
   * emailed as a download link, because an unbounded audit query is not something to hold a
   * browser request open for.
   */
  export({ formatType = 'csv', ...filters } = {}) {
    const params = buildParams(filters);
    params.append('format_type', formatType);
    // Accounts have no timezone of their own, so the file is rendered in the zone of whoever
    // asked for it. A raw UTC column travels alongside it.
    params.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    return axios.post(`${this.url}/export?${params.toString()}`);
  }

  /** Filter options derived from what this account actually has, so the UI never offers an
   * empty filter. */
  filters() {
    return axios.get(`${this.url}/filters`);
  }

  /**
   * TEMPORARY / TESTING ONLY — synchronous download, same filters as export() but returned
   * in-request instead of via the async job + emailed link. See AuditEventsController#download.
   */
  download({ formatType = 'csv', ...filters } = {}) {
    const params = buildParams(filters);
    params.append('format_type', formatType);
    params.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    return axios.get(`${this.url}/download?${params.toString()}`, {
      responseType: 'blob',
    });
  }
}

export default new AuditEvents();
